import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export type MeetingStatus = "preparation" | "scheduled" | "completed" | "cancelled";
export type ParticipantKind = "regular" | "guest";
export type MeetingRole = "chair" | "secretary" | "gabay" | "member" | "guest";
export type ActionStatus = "open" | "in_progress" | "done" | "cancelled";

export type MeetingParticipant = {
  id?: string;
  userId?: string | null;
  guestName?: string | null;
  guestEmail?: string | null;
  participantKind: ParticipantKind | string;
  meetingRole: MeetingRole | string;
  attended?: boolean;
  sortOrder?: number;
  displayName?: string | null;
  email?: string | null;
};

export type MeetingAgendaItem = {
  id: string;
  meetingId?: string;
  title: string;
  notes?: string | null;
  submittedByUserId?: string | null;
  submittedByName?: string | null;
  sortOrder?: number;
  wasDiscussed?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type MeetingTopic = {
  id: string;
  meetingId?: string;
  agendaItemId?: string | null;
  title: string;
  summary?: string | null;
  decisions?: string | null;
  sortOrder?: number;
};

export type MeetingActionItem = {
  id: string;
  meetingId?: string;
  topicId?: string | null;
  description: string;
  technicalDetails?: string | null;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  dueDate?: string | null;
  status?: ActionStatus | string;
  createTask?: boolean;
  linkedTaskId?: string | null;
  completedAt?: string | null;
  verifiedAt?: string | null;
  verifiedByUserId?: string | null;
};

export type MeetingListItem = {
  id: string;
  title: string;
  scheduledAt: string | null;
  durationMinutes: number;
  status: MeetingStatus | string;
  previousMeetingId?: string | null;
  reminderMinutesBefore?: number;
  reminderSentAt?: string | null;
  summarySentAt?: string | null;
  createdByUserId?: string;
  createdByName?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  agendaCount?: number;
  participantsCount?: number;
};

export type MeetingFull = MeetingListItem & {
  participants: MeetingParticipant[];
  agendaItems: MeetingAgendaItem[];
  topics: MeetingTopic[];
  actionItems: MeetingActionItem[];
};

export type AssignableUser = {
  id: string;
  name: string;
  email: string;
};

export type DefaultParticipant = {
  id?: string;
  userId: string;
  meetingRole: MeetingRole | string;
  participantKind?: ParticipantKind | string;
  sortOrder?: number;
  userName?: string | null;
  userEmail?: string | null;
};

export type MeetingsPagination = {
  total: number;
  page: number;
  pageSize: number;
};

export type FinalizeTopicInput = {
  clientKey?: string;
  id?: string;
  agendaItemId?: string | null;
  title?: string;
  summary?: string | null;
  decisions?: string | null;
  sortOrder?: number;
};

export type FinalizeActionInput = {
  topicId?: string | null;
  topicKey?: string;
  description: string;
  technicalDetails?: string | null;
  assignedToUserId?: string | null;
  dueDate?: string | null;
  status?: ActionStatus | string;
  createTask?: boolean;
};

export const meetingsApi = {
  list(params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ items: MeetingListItem[]; pagination: MeetingsPagination }> {
    return wrap(async () => {
      const res = await httpClient.get<{
        items: MeetingListItem[];
        pagination: MeetingsPagination;
      }>("/meetings", { params });
      return res.data;
    });
  },

  getCurrent(): Promise<MeetingFull | null> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: MeetingFull | null }>("/meetings/current");
      return res.data.item;
    });
  },

  getById(id: string): Promise<MeetingFull> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: MeetingFull }>(`/meetings/${id}`);
      return res.data.item;
    });
  },

  create(body: {
    title?: string;
    scheduledAt?: string | null;
    durationMinutes?: number;
    previousMeetingId?: string | null;
    reminderMinutesBefore?: number;
  }): Promise<MeetingFull> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: MeetingFull }>("/meetings", body);
      return res.data.item;
    });
  },

  update(
    id: string,
    body: Partial<{
      title: string;
      scheduledAt: string | null;
      durationMinutes: number;
      status: MeetingStatus | string;
      reminderMinutesBefore: number;
      previousMeetingId: string | null;
    }>,
  ): Promise<MeetingFull> {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: MeetingFull }>(`/meetings/${id}`, body);
      return res.data.item;
    });
  },

  addAgendaItem(
    meetingId: string,
    body: { title: string; notes?: string | null },
  ): Promise<MeetingAgendaItem> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: MeetingAgendaItem }>(
        `/meetings/${meetingId}/agenda`,
        body,
      );
      return res.data.item;
    });
  },

  updateAgendaItem(
    meetingId: string,
    itemId: string,
    body: Partial<{ title: string; notes: string | null; wasDiscussed: boolean; sortOrder: number }>,
  ): Promise<unknown> {
    return wrap(async () => {
      const res = await httpClient.patch(`/meetings/${meetingId}/agenda/${itemId}`, body);
      return res.data;
    });
  },

  deleteAgendaItem(meetingId: string, itemId: string): Promise<unknown> {
    return wrap(async () => {
      const res = await httpClient.delete(`/meetings/${meetingId}/agenda/${itemId}`);
      return res.data;
    });
  },

  moveAgendaItem(
    meetingId: string,
    itemId: string,
    body: { targetMeetingId: string },
  ): Promise<unknown> {
    return wrap(async () => {
      const res = await httpClient.post(
        `/meetings/${meetingId}/agenda/${itemId}/move`,
        body,
      );
      return res.data;
    });
  },

  updateParticipants(
    meetingId: string,
    participants: Array<{
      userId?: string | null;
      guestName?: string | null;
      guestEmail?: string | null;
      participantKind?: string;
      meetingRole?: string;
      attended?: boolean;
      sortOrder?: number;
    }>,
  ): Promise<MeetingFull> {
    return wrap(async () => {
      const res = await httpClient.put<{ item: MeetingFull }>(
        `/meetings/${meetingId}/participants`,
        { participants },
      );
      return res.data.item;
    });
  },

  getDefaultParticipants(): Promise<DefaultParticipant[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: DefaultParticipant[] }>(
        "/meetings/default-participants",
      );
      return res.data.items;
    });
  },

  updateDefaultParticipants(
    participants: Array<{
      userId: string;
      meetingRole?: string;
      participantKind?: string;
      sortOrder?: number;
    }>,
  ): Promise<DefaultParticipant[]> {
    return wrap(async () => {
      const res = await httpClient.put<{ items: DefaultParticipant[] }>(
        "/meetings/default-participants",
        { participants },
      );
      return res.data.items;
    });
  },

  finalizeSummary(
    meetingId: string,
    body: {
      topics?: FinalizeTopicInput[];
      actionItems?: FinalizeActionInput[];
      sendSummary?: boolean;
    },
  ): Promise<MeetingFull> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: MeetingFull }>(
        `/meetings/${meetingId}/finalize-summary`,
        body,
      );
      return res.data.item;
    });
  },

  sendReminder(meetingId: string): Promise<{ message?: string }> {
    return wrap(async () => {
      const res = await httpClient.post<{ message?: string }>(
        `/meetings/${meetingId}/send-reminder`,
      );
      return res.data;
    });
  },

  sendSummary(meetingId: string): Promise<{ message?: string }> {
    return wrap(async () => {
      const res = await httpClient.post<{ message?: string }>(
        `/meetings/${meetingId}/send-summary`,
      );
      return res.data;
    });
  },

  getOpenActions(meetingId: string): Promise<MeetingActionItem[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: MeetingActionItem[] }>(
        `/meetings/${meetingId}/open-actions`,
      );
      return res.data.items;
    });
  },

  updateActionItem(
    actionId: string,
    body: { status?: ActionStatus | string; verified?: boolean },
  ): Promise<{ ok: boolean }> {
    return wrap(async () => {
      const res = await httpClient.patch<{ ok: boolean }>(
        `/meetings/action-items/${actionId}`,
        body,
      );
      return res.data;
    });
  },

  listAssignableUsers(): Promise<AssignableUser[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: AssignableUser[] }>(
        "/meetings/assignable-users",
      );
      return res.data.items;
    });
  },
};
